import './style.scss';

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { XRayGeneratorFaultsHistoryContext } from 'app/providers/XRayGeneratorProvider';
import XRayGeneratorHistoryContext from 'app/providers/XRayGeneratorHistoryProvider/context';
import { Button, Dimmer, Dropdown, Loader, Menu, Modal } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { format } from 'date-fns';
import { getKeyByValue } from 'app/types/xray';
import { GENERATOR_AGGREGATED_ERROR } from 'app/xray/generator/constants';
import { convertExamionCodeToGER } from 'app/adapters/XRayGenerator/xrayGeneratorErrorUtils';

const formatRadParamsDisplay = (params, intl) => {
  if (!params) return '';
  return intl.formatMessage(
    { id: 'xray_generator.fault_history.params_format' },
    { kV: params.kV, mA: params.mA, ms: params.ms, mAs: params.mAs }
  );
};

const formatFaultsToList = (faults, { intl }) =>
  faults
    .map(({ timestamp, type, code, radParams, resultParams }) => {
      try {
        const formattedDate = format(timestamp, 'yyyy-MM-dd kk:mm:ss');
        const gerCode = convertExamionCodeToGER(code);

        const paramsLabel = intl.formatMessage({
          id: 'xray_generator.fault_history.params_label',
        });
        const resultLabel = intl.formatMessage({
          id: 'xray_generator.fault_history.result_label',
        });
        const paramsDisplay = radParams
          ? `${paramsLabel} ${formatRadParamsDisplay(radParams, intl)}`
          : '';
        const resultDisplay = resultParams
          ? `${resultLabel} ${formatRadParamsDisplay(resultParams, intl)}`
          : '';

        const description = `${paramsDisplay}${
          paramsDisplay && resultDisplay ? ' | ' : ''
        }${resultDisplay}`;

        return `[${formattedDate}] ${intl.formatMessage({
          id: `xray_generator.fault_history.${type}`,
        })} ${gerCode.code} : ${description}`;
      } catch (error) {
        console.error(
          'Error formatting error for display',
          { timestamp, type, code, radParams, resultParams },
          error
        );
        return undefined;
      }
    })
    .filter(_.identity);

const formatShotsToList = (shots, { intl }) =>
  shots
    .map(({ date, kV, mAs, mA, ms, operators }) => {
      try {
        const formattedDate = format(date, 'yyyy-MM-dd kk:mm:ss');
        const xRayShotPrefix = intl.formatMessage({
          id: `account.configuration.xray_generator.fault_history.xray_shot_prefix`,
        });
        const kVFormatted = intl.formatMessage(
          {
            id: `general.kV`,
          },
          { kV }
        );
        const mAsFormatted = intl.formatMessage(
          {
            id: `general.mAs`,
          },
          { mAs }
        );
        const mAFormatted = intl.formatMessage(
          {
            id: `general.mA`,
          },
          { mA }
        );
        const msFormatted = intl.formatMessage(
          {
            id: `general.ms`,
          },
          { ms }
        );
        const operatorsFormatted = intl.formatMessage(
          {
            id: operators ? `general.operators` : `general.operators.none`,
          },
          { operators: operators?.join(', ') }
        );

        return `[${formattedDate}] ${xRayShotPrefix} ${kVFormatted}, ${mAsFormatted}, ${mAFormatted}, 
          ${msFormatted}, ${operatorsFormatted}`;
      } catch (error) {
        console.error(
          'Error formatting shot for display',
          { date, kV, mAs, mA, ms, operators },
          error
        );
        return undefined;
      }
    })
    .filter(_.identity);

const formatShotsAndFaultsToList = (shots, faults, { intl }) => {
  const formattedFaults = formatFaultsToList(faults, { intl });
  const formattedShots = formatShotsToList(shots, { intl });

  const formattedShotsAndFaultsDateSorted = [...formattedShots, ...formattedFaults]
    .sort()
    .reverse()
    .map((line, index) => <li key={`${line}${index}`}>{line}</li>);

  return formattedShotsAndFaultsDateSorted.length === 0 ? (
    <FormattedMessage id="account.configuration.xray_generator.fault_history.no_fault" />
  ) : (
    <ul>{formattedShotsAndFaultsDateSorted}</ul>
  );
};

const XRayGeneratorFaultsDisplay = () => {
  const xRayGeneratorFaultsHistory = useContext(XRayGeneratorFaultsHistoryContext);
  const xRayGeneratorHistory = useContext(XRayGeneratorHistoryContext);
  if (!xRayGeneratorFaultsHistory) return null;

  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [faultsLoading, setFaultsLoading] = useState(false);
  const [displayFilter, setDisplayFilter] = useState('all');
  const intl = useIntl();

  const filterOptions = useMemo(
    () => [
      {
        key: 'all',
        text: intl.formatMessage({
          id: 'account.configuration.xray_generator.fault_history.filter.all',
        }),
        value: 'all',
      },
      {
        key: 'faults',
        text: intl.formatMessage({
          id: 'account.configuration.xray_generator.fault_history.filter.faults',
        }),
        value: 'faults',
      },
    ],
    [intl]
  );

  const setupErrorsForDisplay = useCallback(async () => {
    console.debug('setupErrorsForDisplay called with filter', displayFilter);
    setFaultsLoading(true);
    try {
      const faults = await xRayGeneratorFaultsHistory.getFaults();
      const shots = displayFilter === 'all' ? await xRayGeneratorHistory.getAllHistory() : [];
      setContent(formatShotsAndFaultsToList(shots, faults, { intl }));
    } catch (error) {
      console.error('Error loading faults or shots history:', error);
      setContent(
        <strong>
          <FormattedMessage id="account.configuration.xray_generator.fault_history.error" />
        </strong>
      );
    }
    setFaultsLoading(false);
  }, [intl, displayFilter]);

  useEffect(() => {
    if (!isOpen) return;
    setupErrorsForDisplay();
  }, [displayFilter, setupErrorsForDisplay, isOpen]);

  return (
    <Modal
      trigger={
        <Button>
          <FormattedMessage id="account.configuration.xray_generator.fault_history.display" />
        </Button>
      }
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      open={isOpen}
      closeIcon
      className="fault-display"
    >
      <Modal.Header
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <FormattedMessage id="account.configuration.xray_generator.fault_history.header" />
        <Dropdown
          button
          className="icon"
          labeled
          icon="filter"
          options={filterOptions}
          value={displayFilter}
          onChange={(e, { value }) => setDisplayFilter(value)}
          simple
          item
        />
      </Modal.Header>
      <Modal.Content style={{ overflowY: 'auto' }}>
        <Dimmer.Dimmable dimmed={faultsLoading}>
          <Dimmer active={faultsLoading}>
            <Loader />
          </Dimmer>
          {content}
        </Dimmer.Dimmable>
      </Modal.Content>
    </Modal>
  );
};

export default React.memo(XRayGeneratorFaultsDisplay);
